"use client";

export type AuditApiV2Config = {
  baseUrl: string;
  internalApiKey: string;
};

const DEFAULT_BASE_URL =
  "https://bcjmfypqlghiwpliasyw.supabase.co/functions/v1/audit-api-v2/v2/api/audit";

export function getAuditApiV2Config(): AuditApiV2Config {
  const baseUrl = String(import.meta.env.VITE_AUDIT_API_V2_BASE_URL || DEFAULT_BASE_URL).replace(/\/+$/, "");
  const internalApiKey = String(import.meta.env.VITE_AUDIT_INTERNAL_API_KEY || "");
  return { baseUrl, internalApiKey };
}

export function buildAuditApiV2Url(path: string, query?: Record<string, string | number | boolean | null | undefined>) {
  const { baseUrl } = getAuditApiV2Config();
  const p = String(path || "").replace(/^\/+/, "");
  const url = new URL(`${baseUrl}/${p}`);
  if (query) {
    for (const [k, v] of Object.entries(query)) {
      if (v === undefined || v === null) continue;
      url.searchParams.set(k, String(v));
    }
  }
  return url.toString();
}

export async function auditApiV2Get<T = unknown>(
  path: string,
  query?: Record<string, string | number | boolean | null | undefined>
): Promise<T> {
  const { internalApiKey } = getAuditApiV2Config();
  if (!internalApiKey) {
    throw new Error("Audit API key not configured (missing VITE_AUDIT_INTERNAL_API_KEY).");
  }

  const url = buildAuditApiV2Url(path, query);
  const resp = await fetch(url, {
    method: "GET",
    headers: {
      "x-internal-api-key": internalApiKey,
    },
  });

  if (!resp.ok) {
    const text = await resp.text().catch(() => "");
    throw new Error(`Request failed (${resp.status})${text ? `: ${text}` : ""}`);
  }

  const parsed = (await resp.json()) as unknown;
  if (parsed && typeof parsed === "object" && !Array.isArray(parsed) && "error" in (parsed as Record<string, unknown>)) {
    const rawError = (parsed as Record<string, unknown>).error;
    const errorSummary =
      typeof rawError === "string"
        ? rawError
        : JSON.stringify(rawError ?? "Unknown API error");
    throw new Error(`Audit API error: ${errorSummary}`);
  }
  return parsed as T;
}

